'use client';

// @TODO: move this file to shared package

import {
  AuthUser,
  fetchAuthSession,
  fetchUserAttributes,
  FetchUserAttributesOutput,
  getCurrentUser as getCurrentUserAmplify,
  signOut,
} from 'aws-amplify/auth';
import { generateClient, get, post, del, patch } from 'aws-amplify/api';
import { v4 as uuidv4 } from 'uuid';
import { reactQueryClient } from '../react-query/query-client';

export const gqlClient = generateClient();

const currentRequestPromisesMap = new Map();

export async function graphqlRequest(...args: any[]): Promise<any> {
  const key = uuidv4();

  try {
    const newRequest = gqlClient.graphql(args[0]);

    currentRequestPromisesMap.set(key, newRequest);

    const promise = await newRequest;

    currentRequestPromisesMap.delete(key);

    return promise;
  } catch (err: any) {
    currentRequestPromisesMap.delete(key);

    console.log('cannot complete your request', err);
    throw err;
  }
}

export function postRequest(...args: any[]): Promise<any> {
  return safeRequest(post, args);
}

export function getRequest(...args: any[]): Promise<any> {
  return safeRequest(get, args);
}

export function deleteRequest(...args: any[]): Promise<any> {
  return safeRequest(del, args);
}

export function patchRequest(...args: any[]): Promise<any> {
  return safeRequest(patch, args);
}

export async function getAccessToken() {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken;
  } catch (err) {
    // console.log(err);
    return '';
  }
}

export async function getCustomHeader() {
  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      return { Authorization: '' };
    }

    return {
      Authorization: `Bearer ${accessToken.toString()}`,
    };
  } catch (err) {
    console.log(err);
    return { Authorization: '' };
  }
}

async function safeRequest(func, args) {
  const key = uuidv4();

  try {
    const newRequest = func(...args);

    currentRequestPromisesMap.set(key, newRequest);

    const promise = await newRequest.response;

    currentRequestPromisesMap.delete(key);
    if (promise.statusCode === 200) {
      return await promise.body.json();
    }

    return true;
  } catch (err: any) {
    currentRequestPromisesMap.delete(key);

    console.log('cannot complete your request', err);
    throw err;
  }
}

export const getCurrentUser = async (): Promise<AuthUser | null> => {
  try {
    const user = await reactQueryClient.fetchQuery({
      queryKey: ['amplify-auth', 'current-user'],
      queryFn: () => getCurrentUserAmplify(),
      staleTime: Infinity,
    });
    return user;
  } catch (err) {
    // console.log(err);
    return null;
  }
};

export const getIsLoggedIn = async () => {
  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
};

export const getUserAttributes = async (): Promise<FetchUserAttributesOutput | null> => {
  try {
    const attributes = await reactQueryClient.fetchQuery({
      queryKey: ['amplify-auth', 'user-attributes'],
      queryFn: () => fetchUserAttributes(),
      staleTime: Infinity,
    });
    return attributes;
  } catch (err) {
    // console.log(err);
    return null;
  }
};

export const getAuthenticatedUserData: () => Promise<
  (Omit<AuthUser, 'signInDetails'> & AuthUser['signInDetails'] & { attributes: FetchUserAttributesOutput }) | null
> = async () => {
  try {
    const user = await getCurrentUser();
    if (!user) {
      return null;
    }
    const { signInDetails, ...userData } = user;

    const attributes = await getUserAttributes();
    if (!attributes) {
      return null;
    }
    return {
      ...userData,
      ...signInDetails,
      attributes,
    };
  } catch (err) {
    // console.log(err);
    return null;
  }
};

export const handleSignOut = async () => {
  await signOut();
  reactQueryClient.removeQueries({ queryKey: ['amplify-auth'] });
};
